import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import BillingReport from './components/BillingReport';
import './Dashboard.css';

const Dashboard = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyFilterStatus, setHistoryFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const [customerTotalPages, setCustomerTotalPages] = useState(1);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showLabelsMenu, setShowLabelsMenu] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedDivisions, setExpandedDivisions] = useState({});
  const [expandedTeams, setExpandedTeams] = useState({});
  const [customerFormData, setCustomerFormData] = useState({
    cust_name: '',
    contact: '',
    phone: '',
    addr1: '',
    addr2: '',
    city: '',
    state: '',
    zip: '',
  });
  const itemsPerPage = 10;

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  // Fetch customers when Customers tab is active
  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    }
  }, [activeTab, customerSearchTerm, customerCurrentPage]);

  // Fetch order history when Order-History tab is active
  useEffect(() => {
    if (activeTab === 'order-history') {
      fetchOrderHistory();
    }
  }, [activeTab, historySearchTerm, historyFilterStatus, historyCurrentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/uniform-orders/orders', {
        params: { limit: 1000 },
      });
      const ordersList = response.data.orders || [];
      setOrders(ordersList);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch orders';
      console.error('Fetch orders error:', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders based on status and search
  const filteredOrders = orders.filter((order) => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;

    // If no search term, check status only
    if (!searchTerm.trim()) {
      return matchesStatus;
    }

    // Search across multiple fields
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (order.customer_name && order.customer_name.toLowerCase().includes(searchLower)) ||
      (order.team_name && order.team_name.toLowerCase().includes(searchLower)) ||
      (order.order_number && order.order_number.toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch;
  });

  // Paginate orders
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  const handleCreateOrder = () => {
    navigate('/league-uniform-management/order-creation');
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
  };

  const handleCloseOrderDetailsModal = () => {
    setShowOrderDetailsModal(false);
    setShowPrintMenu(false);
    setShowLabelsMenu(false);
    setSelectedOrder(null);
    setExpandedDivisions({});
    setExpandedTeams({});
  };

  // Toggle division expansion
  const toggleDivision = (divIdx) => {
    setExpandedDivisions(prev => ({
      ...prev,
      [divIdx]: !prev[divIdx]
    }));
  };

  // Toggle team expansion
  const toggleTeam = (divIdx, teamIdx) => {
    const key = `${divIdx}-${teamIdx}`;
    setExpandedTeams(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get available print categories based on order details
  const getAvailablePrintCategories = (orderDetails) => {
    if (!orderDetails) return [];

    const categories = [];

    // Check for jerseys - both new divisions/teams structure and legacy flat structure
    let hasJerseys = false;
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      // New divisions/teams structure
      hasJerseys = orderDetails.divisions.some(division =>
        division.teams?.some(team =>
          team.jerseys?.some(j =>
            j.youth?.quantity > 0 || j.adult?.quantity > 0 || j.coach?.quantity > 0
          )
        )
      );
    } else if (orderDetails.jerseys) {
      // Legacy flat structure
      hasJerseys = orderDetails.jerseys.some(j =>
        j.youth?.quantity > 0 || j.adult?.quantity > 0 || j.coach?.quantity > 0
      );
    }
    if (hasJerseys) {
      categories.push({ key: 'jerseys', label: 'Jerseys', icon: '👕' });
    }

    // Check for pants - both structures
    let hasPants = false;
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      hasPants = orderDetails.divisions.some(division =>
        division.teams?.some(team =>
          team.pants && (team.pants.youth?.quantity > 0 || team.pants.adult?.quantity > 0)
        )
      );
    } else if (orderDetails.pants) {
      hasPants = orderDetails.pants.youth?.quantity > 0 || orderDetails.pants.adult?.quantity > 0;
    }
    if (hasPants) {
      categories.push({ key: 'pants', label: 'Pants', icon: '👖' });
    }

    // Check for caps - both structures
    let hasCaps = false;
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      hasCaps = orderDetails.divisions.some(division =>
        division.teams?.some(team =>
          team.caps && (team.caps.youth?.quantity > 0 || team.caps.adult?.quantity > 0)
        )
      );
    } else if (orderDetails.caps) {
      hasCaps = orderDetails.caps.youth?.quantity > 0 || orderDetails.caps.adult?.quantity > 0;
    }
    if (hasCaps) {
      categories.push({ key: 'caps', label: 'Caps', icon: '🧢' });
    }

    // Check for belts - both structures (belts have youth/adult sub-objects)
    let hasBelts = false;
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      hasBelts = orderDetails.divisions.some(division =>
        division.teams?.some(team =>
          team.belts && ((team.belts.youth?.quantity > 0) || (team.belts.adult?.quantity > 0))
        )
      );
    } else if (orderDetails.belts) {
      hasBelts = (orderDetails.belts.youth?.quantity > 0) || (orderDetails.belts.adult?.quantity > 0);
    }
    if (hasBelts) {
      categories.push({ key: 'belts', label: 'Belts', icon: '🥋' });
    }

    // Check for socks - both structures
    let hasSocks = false;
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      hasSocks = orderDetails.divisions.some(division =>
        division.teams?.some(team =>
          team.socks && team.socks.quantity > 0
        )
      );
    } else if (orderDetails.socks) {
      hasSocks = orderDetails.socks.quantity > 0;
    }
    if (hasSocks) {
      categories.push({ key: 'socks', label: 'Socks', icon: '🧦' });
    }

    return categories;
  };

  // Handle blank order form generation (hand-fillable packet, not tied to an existing order)
  const handlePrintBlankForm = async () => {
    try {
      const teamsInput = window.prompt(
        'How many teams are on this order?\n\nLeave blank for a standard small-league form. For a big league (10+ teams), entering a number sizes every page to fit your whole roster.'
      );
      const params = {};
      if (teamsInput && teamsInput.trim()) {
        const teams = parseInt(teamsInput.trim(), 10);
        if (!Number.isNaN(teams) && teams > 0) {
          params.teams = teams;
        }
      }

      const response = await api.get('/uniform-orders/blank-form', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Blank order form error:', err);
      alert('Failed to generate blank order form. Please try again.');
    }
  };

  // Handle print form generation
  const handlePrintForm = async (category) => {
    try {
      const response = await api.get(`/uniform-orders/orders/${selectedOrder.id}/print/${category}`, {
        responseType: 'blob'
      });

      // Create blob URL and open in new window for printing
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      setShowPrintMenu(false);
    } catch (err) {
      console.error('Print form error:', err);
      alert(`Failed to generate ${category} form. Please try again.`);
    }
  };

  // Handle print labels generation
  const handlePrintLabels = async (category) => {
    try {
      const response = await api.get(`/uniform-orders/orders/${selectedOrder.id}/labels/${category}`, {
        responseType: 'blob'
      });

      // Create blob URL and open in new window for printing
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      setShowLabelsMenu(false);
    } catch (err) {
      console.error('Print labels error:', err);
      alert(`Failed to generate ${category} labels. Please try again.`);
    }
  };

  const handleDeleteOrder = async (order) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete order "${order.order_number}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/uniform-orders/orders/${order.id}`);
      handleCloseOrderDetailsModal();
      fetchOrders();
      alert('Order deleted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete order';
      console.error('Delete order error:', err);
      alert(errorMsg);
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Helper function to calculate total price from order_details when total_price is 0
  const calculateTotalFromDetails = (orderDetails) => {
    if (!orderDetails) return 0;

    let total = 0;

    // Check for new divisions/teams structure
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      orderDetails.divisions.forEach(division => {
        division.teams?.forEach(team => {
          const teamTotals = calculateTeamTotalsStatic(team);
          total += teamTotals.price;
        });
      });
      return total;
    }

    // Legacy flat structure
    // Sum jerseys totals
    if (orderDetails.jerseys && Array.isArray(orderDetails.jerseys)) {
      orderDetails.jerseys.forEach(jersey => {
        total += parseFloat(jersey.youth?.total) || 0;
        total += parseFloat(jersey.adult?.total) || 0;
        total += parseFloat(jersey.coach?.total) || 0;
      });
    }

    // Sum pants totals
    if (orderDetails.pants) {
      total += parseFloat(orderDetails.pants.youth?.total) || 0;
      total += parseFloat(orderDetails.pants.adult?.total) || 0;
    }

    // Sum belts total
    if (orderDetails.belts) {
      total += parseFloat(orderDetails.belts.total) || 0;
    }

    // Sum socks totals
    if (orderDetails.socks) {
      if (orderDetails.socks.total) {
        total += parseFloat(orderDetails.socks.total) || 0;
      } else if (orderDetails.socks.sizes) {
        Object.values(orderDetails.socks.sizes).forEach(size => {
          total += parseFloat(size.total) || 0;
        });
      }
    }

    // Sum caps totals
    if (orderDetails.caps) {
      total += parseFloat(orderDetails.caps.youth?.total) || 0;
      total += parseFloat(orderDetails.caps.adult?.total) || 0;
    }

    return total;
  };

  // Helper function to calculate total items from order_details
  const calculateTotalItemsFromDetails = (orderDetails) => {
    if (!orderDetails) return 0;

    let items = 0;

    // Check for new divisions/teams structure
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      orderDetails.divisions.forEach(division => {
        division.teams?.forEach(team => {
          const teamTotals = calculateTeamTotalsStatic(team);
          items += teamTotals.items;
        });
      });
      return items;
    }

    // Legacy flat structure
    if (orderDetails.jerseys && Array.isArray(orderDetails.jerseys)) {
      orderDetails.jerseys.forEach(jersey => {
        items += (jersey.youth?.quantity || 0) + (jersey.adult?.quantity || 0) + (jersey.coach?.quantity || 0);
      });
    }
    if (orderDetails.pants) {
      items += (orderDetails.pants.youth?.quantity || 0) + (orderDetails.pants.adult?.quantity || 0);
    }
    if (orderDetails.belts) {
      items += (orderDetails.belts.youth?.quantity || 0) + (orderDetails.belts.adult?.quantity || 0);
    }
    if (orderDetails.socks) {
      items += orderDetails.socks.quantity || 0;
    }
    if (orderDetails.caps) {
      items += (orderDetails.caps.youth?.quantity || 0) + (orderDetails.caps.adult?.quantity || 0);
    }

    return items;
  };

  // Static version of calculateTeamTotals (doesn't depend on state)
  const calculateTeamTotalsStatic = (team) => {
    let items = 0;
    let price = 0;

    // Jerseys
    if (team.jerseys) {
      team.jerseys.forEach(jersey => {
        items += (jersey.youth?.quantity || 0) + (jersey.adult?.quantity || 0) + (jersey.coach?.quantity || 0);
        price += (parseFloat(jersey.youth?.total) || 0) + (parseFloat(jersey.adult?.total) || 0) + (parseFloat(jersey.coach?.total) || 0);
        price += parseFloat(jersey.sponsor_total) || 0;
      });
    }

    // Pants
    if (team.pants) {
      items += (team.pants.youth?.quantity || 0) + (team.pants.adult?.quantity || 0);
      price += (parseFloat(team.pants.youth?.total) || 0) + (parseFloat(team.pants.adult?.total) || 0);
    }

    // Belts (has youth/adult sub-objects)
    if (team.belts) {
      items += (team.belts.youth?.quantity || 0) + (team.belts.adult?.quantity || 0);
      price += (parseFloat(team.belts.youth?.total) || 0) + (parseFloat(team.belts.adult?.total) || 0);
    }

    // Socks
    if (team.socks) {
      items += team.socks.quantity || 0;
      price += parseFloat(team.socks.total) || 0;
    }

    // Caps
    if (team.caps) {
      items += (team.caps.youth?.quantity || 0) + (team.caps.adult?.quantity || 0);
      price += (parseFloat(team.caps.youth?.total) || 0) + (parseFloat(team.caps.adult?.total) || 0);
    }

    return { items, price };
  };

  // Get order total - use total_price if available, otherwise calculate from order_details
  const getOrderTotal = (order) => {
    const totalPrice = parseFloat(order.total_price) || 0;
    if (totalPrice > 0) return totalPrice;
    return calculateTotalFromDetails(order.order_details);
  };

  // Get order item count - use quantity if available, otherwise calculate from order_details
  const getOrderItemCount = (order) => {
    const quantity = parseInt(order.quantity) || 0;
    if (quantity > 0) return quantity;
    return calculateTotalItemsFromDetails(order.order_details);
  };

  // Helper function to calculate team totals
  const calculateTeamTotals = (team) => {
    let items = 0;
    let price = 0;

    // Jerseys
    if (team.jerseys) {
      team.jerseys.forEach(jersey => {
        items += (jersey.youth?.quantity || 0) + (jersey.adult?.quantity || 0) + (jersey.coach?.quantity || 0);
        price += (parseFloat(jersey.youth?.total) || 0) + (parseFloat(jersey.adult?.total) || 0) + (parseFloat(jersey.coach?.total) || 0);
        price += parseFloat(jersey.sponsor_total) || 0;
      });
    }

    // Pants
    if (team.pants) {
      items += (team.pants.youth?.quantity || 0) + (team.pants.adult?.quantity || 0);
      price += (parseFloat(team.pants.youth?.total) || 0) + (parseFloat(team.pants.adult?.total) || 0);
    }

    // Belts (has youth/adult sub-objects)
    if (team.belts) {
      items += (team.belts.youth?.quantity || 0) + (team.belts.adult?.quantity || 0);
      price += (parseFloat(team.belts.youth?.total) || 0) + (parseFloat(team.belts.adult?.total) || 0);
    }

    // Socks
    if (team.socks) {
      items += team.socks.quantity || 0;
      price += parseFloat(team.socks.total) || 0;
    }

    // Caps
    if (team.caps) {
      items += (team.caps.youth?.quantity || 0) + (team.caps.adult?.quantity || 0);
      price += (parseFloat(team.caps.youth?.total) || 0) + (parseFloat(team.caps.adult?.total) || 0);
    }

    return { items, price };
  };

  // Helper function to render team products summary
  const renderTeamProducts = (team) => {
    const products = [];

    // Jerseys
    if (team.jerseys && team.jerseys.length > 0) {
      team.jerseys.forEach((jersey, idx) => {
        const jerseyQty = (jersey.youth?.quantity || 0) + (jersey.adult?.quantity || 0) + (jersey.coach?.quantity || 0);
        if (jerseyQty > 0) {
          products.push(
            <div key={`jersey-${idx}`} className="product-line">
              <span className="product-icon">👕</span>
              <span className="product-name">Jersey {jersey.color && `(${jersey.color})`}</span>
              <span className="product-qty">{jerseyQty} pcs</span>
            </div>
          );
        }
      });
    }

    // Pants
    if (team.pants && ((team.pants.youth?.quantity || 0) + (team.pants.adult?.quantity || 0)) > 0) {
      const pantsQty = (team.pants.youth?.quantity || 0) + (team.pants.adult?.quantity || 0);
      products.push(
        <div key="pants" className="product-line">
          <span className="product-icon">👖</span>
          <span className="product-name">Pants {team.pants.color && `(${team.pants.color})`}</span>
          <span className="product-qty">{pantsQty} pcs</span>
        </div>
      );
    }

    // Belts (has youth/adult sub-objects)
    if (team.belts && ((team.belts.youth?.quantity || 0) + (team.belts.adult?.quantity || 0)) > 0) {
      const beltsQty = (team.belts.youth?.quantity || 0) + (team.belts.adult?.quantity || 0);
      const beltColor = team.belts.youth?.color || team.belts.adult?.color || '';
      products.push(
        <div key="belts" className="product-line">
          <span className="product-icon">🥋</span>
          <span className="product-name">Belts {beltColor && `(${beltColor})`}</span>
          <span className="product-qty">{beltsQty} pcs</span>
        </div>
      );
    }

    // Socks
    if (team.socks && team.socks.quantity > 0) {
      products.push(
        <div key="socks" className="product-line">
          <span className="product-icon">🧦</span>
          <span className="product-name">Socks {team.socks.color && `(${team.socks.color})`}</span>
          <span className="product-qty">{team.socks.quantity} pcs</span>
        </div>
      );
    }

    // Caps
    if (team.caps && ((team.caps.youth?.quantity || 0) + (team.caps.adult?.quantity || 0)) > 0) {
      const capsQty = (team.caps.youth?.quantity || 0) + (team.caps.adult?.quantity || 0);
      products.push(
        <div key="caps" className="product-line">
          <span className="product-icon">🧢</span>
          <span className="product-name">Caps {team.caps.cap_color && `(${team.caps.cap_color})`}</span>
          <span className="product-qty">{capsQty} pcs</span>
        </div>
      );
    }

    return products.length > 0 ? products : <span className="no-products">No products</span>;
  };

  // Helper function to render product breakdown
  const renderOrderBreakdown = (orderDetails) => {
    if (!orderDetails) return <p>No product details available</p>;

    // Check if this is the new divisions/teams structure
    if (orderDetails.divisions && orderDetails.divisions.length > 0) {
      return (
        <div className="divisions-breakdown accordion-container">
          {orderDetails.divisions.map((division, divIdx) => {
            // Calculate division totals
            let divisionItems = 0;
            let divisionPrice = 0;
            division.teams?.forEach(team => {
              const totals = calculateTeamTotals(team);
              divisionItems += totals.items;
              divisionPrice += totals.price;
            });

            const isDivisionExpanded = expandedDivisions[divIdx];

            return (
              <div key={divIdx} className={`division-accordion ${isDivisionExpanded ? 'expanded' : ''}`}>
                <div
                  className="division-accordion-header"
                  onClick={() => toggleDivision(divIdx)}
                >
                  <div className="accordion-toggle">
                    <span className="accordion-icon">{isDivisionExpanded ? '▼' : '▶'}</span>
                    <h4>{division.name || `Division ${divIdx + 1}`}</h4>
                  </div>
                  <div className="division-stats">
                    <span className="stat">{division.teams?.length || 0} teams</span>
                    <span className="stat">{divisionItems} items</span>
                    <span className="stat price">{formatCurrency(divisionPrice)}</span>
                  </div>
                </div>
                {isDivisionExpanded && (
                  <div className="division-accordion-content">
                    <div className="teams-breakdown">
                      {division.teams?.map((team, teamIdx) => {
                        const teamTotals = calculateTeamTotals(team);
                        const teamKey = `${divIdx}-${teamIdx}`;
                        const isTeamExpanded = expandedTeams[teamKey];

                        return (
                          <div key={teamIdx} className={`team-accordion ${isTeamExpanded ? 'expanded' : ''}`}>
                            <div
                              className="team-accordion-header"
                              onClick={() => toggleTeam(divIdx, teamIdx)}
                            >
                              <div className="accordion-toggle">
                                <span className="accordion-icon">{isTeamExpanded ? '▼' : '▶'}</span>
                                <div className="team-info">
                                  <span className="team-name">{team.team_name || `Team ${teamIdx + 1}`}</span>
                                  {team.sponsor_name && <span className="team-sponsor">Sponsor: {team.sponsor_name}</span>}
                                </div>
                              </div>
                              <div className="team-totals">
                                <span className="team-items">{teamTotals.items} items</span>
                                <span className="team-price">{formatCurrency(teamTotals.price)}</span>
                              </div>
                            </div>
                            {isTeamExpanded && (
                              <div className="team-accordion-content">
                                <div className="team-products">
                                  {renderTeamProducts(team)}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Legacy format - flat structure without divisions
    const sections = [];

    // Jerseys
    if (orderDetails.jerseys && orderDetails.jerseys.length > 0) {
      const jerseyItems = orderDetails.jerseys.filter(j =>
        j.youth?.quantity > 0 || j.adult?.quantity > 0 || j.coach?.quantity > 0
      );
      if (jerseyItems.length > 0) {
        sections.push(
          <div key="jerseys" className="breakdown-section">
            <h4>Jerseys</h4>
            {jerseyItems.map((jersey, idx) => (
              <div key={idx} className="breakdown-item">
                <div className="item-header">
                  <span className="item-title">Jersey #{jersey.jersey_number}</span>
                  {jersey.jersey_type && <span className="item-detail">{jersey.jersey_type}</span>}
                  {jersey.color && <span className="item-detail">({jersey.color})</span>}
                </div>
                {jersey.youth?.quantity > 0 && (
                  <div className="item-line">
                    <span>Youth: {jersey.youth.quantity} × {formatCurrency(jersey.youth.unit_price)}</span>
                    <span className="line-total">{formatCurrency(jersey.youth.total)}</span>
                  </div>
                )}
                {jersey.adult?.quantity > 0 && (
                  <div className="item-line">
                    <span>Adult: {jersey.adult.quantity} × {formatCurrency(jersey.adult.unit_price)}</span>
                    <span className="line-total">{formatCurrency(jersey.adult.total)}</span>
                  </div>
                )}
                {jersey.coach?.quantity > 0 && (
                  <div className="item-line">
                    <span>Coach: {jersey.coach.quantity} × {formatCurrency(jersey.coach.unit_price)}</span>
                    <span className="line-total">{formatCurrency(jersey.coach.total)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }
    }

    // Pants
    if (orderDetails.pants && (orderDetails.pants.youth?.quantity > 0 || orderDetails.pants.adult?.quantity > 0)) {
      sections.push(
        <div key="pants" className="breakdown-section">
          <h4>Pants</h4>
          <div className="breakdown-item">
            {orderDetails.pants.type && <div className="item-header">{orderDetails.pants.type} {orderDetails.pants.color && `(${orderDetails.pants.color})`}</div>}
            {orderDetails.pants.youth?.quantity > 0 && (
              <div className="item-line">
                <span>Youth: {orderDetails.pants.youth.quantity} × {formatCurrency(orderDetails.pants.youth.unit_price)}</span>
                <span className="line-total">{formatCurrency(orderDetails.pants.youth.total)}</span>
              </div>
            )}
            {orderDetails.pants.adult?.quantity > 0 && (
              <div className="item-line">
                <span>Adult: {orderDetails.pants.adult.quantity} × {formatCurrency(orderDetails.pants.adult.unit_price)}</span>
                <span className="line-total">{formatCurrency(orderDetails.pants.adult.total)}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Belts (has youth/adult sub-objects)
    if (orderDetails.belts && ((orderDetails.belts.youth?.quantity > 0) || (orderDetails.belts.adult?.quantity > 0))) {
      sections.push(
        <div key="belts" className="breakdown-section">
          <h4>Belts</h4>
          <div className="breakdown-item">
            {orderDetails.belts.youth?.quantity > 0 && (
              <>
                {orderDetails.belts.youth.color && <div className="item-header">Youth - {orderDetails.belts.youth.color}</div>}
                <div className="item-line">
                  <span>Youth: {orderDetails.belts.youth.quantity} × {formatCurrency(orderDetails.belts.youth.unit_price)}</span>
                  <span className="line-total">{formatCurrency(orderDetails.belts.youth.total)}</span>
                </div>
              </>
            )}
            {orderDetails.belts.adult?.quantity > 0 && (
              <>
                {orderDetails.belts.adult.color && <div className="item-header">Adult - {orderDetails.belts.adult.color}</div>}
                <div className="item-line">
                  <span>Adult: {orderDetails.belts.adult.quantity} × {formatCurrency(orderDetails.belts.adult.unit_price)}</span>
                  <span className="line-total">{formatCurrency(orderDetails.belts.adult.total)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Socks
    if (orderDetails.socks && orderDetails.socks.quantity > 0) {
      sections.push(
        <div key="socks" className="breakdown-section">
          <h4>Socks</h4>
          <div className="breakdown-item">
            {orderDetails.socks.sock_type && <div className="item-header">{orderDetails.socks.sock_type} {orderDetails.socks.color && `(${orderDetails.socks.color})`}</div>}
            {orderDetails.socks.sizes && Object.entries(orderDetails.socks.sizes).map(([size, data]) => (
              data.quantity > 0 && (
                <div key={size} className="item-line">
                  <span>Size {size}: {data.quantity} × {formatCurrency(data.unit_price)}</span>
                  <span className="line-total">{formatCurrency(data.total)}</span>
                </div>
              )
            ))}
          </div>
        </div>
      );
    }

    // Caps
    if (orderDetails.caps && (orderDetails.caps.youth?.quantity > 0 || orderDetails.caps.adult?.quantity > 0)) {
      sections.push(
        <div key="caps" className="breakdown-section">
          <h4>Caps</h4>
          <div className="breakdown-item">
            {orderDetails.caps.cap_type && <div className="item-header">{orderDetails.caps.cap_type} {orderDetails.caps.cap_color && `(${orderDetails.caps.cap_color})`}</div>}
            {orderDetails.caps.youth?.quantity > 0 && (
              <div className="item-line">
                <span>Youth: {orderDetails.caps.youth.quantity} × {formatCurrency(orderDetails.caps.youth.unit_price)}</span>
                <span className="line-total">{formatCurrency(orderDetails.caps.youth.total)}</span>
              </div>
            )}
            {orderDetails.caps.adult?.quantity > 0 && (
              <div className="item-line">
                <span>Adult: {orderDetails.caps.adult.quantity} × {formatCurrency(orderDetails.caps.adult.unit_price)}</span>
                <span className="line-total">{formatCurrency(orderDetails.caps.adult.total)}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return sections.length > 0 ? sections : <p>No products in this order</p>;
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/league-uniform-customers', {
        params: {
          search: customerSearchTerm,
          page: customerCurrentPage,
          limit: itemsPerPage,
        },
      });
      setCustomers(response.data.customers || []);
      setCustomerTotalPages(response.data.pagination?.totalPages || 1);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch customers';
      console.error('Fetch customers error:', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = () => {
    setShowAddCustomerModal(true);
  };

  const handleCloseCustomerModal = () => {
    setShowAddCustomerModal(false);
    setCustomerFormData({
      cust_name: '',
      contact: '',
      phone: '',
      addr1: '',
      addr2: '',
      city: '',
      state: '',
      zip: '',
    });
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitCustomer = async (e) => {
    e.preventDefault();

    // Validate required field
    if (!customerFormData.cust_name.trim()) {
      alert('League name is required');
      return;
    }

    try {
      setLoading(true);
      await api.post('/league-uniform-customers', customerFormData);

      // Close modal and reset form
      handleCloseCustomerModal();

      // Refresh customer list
      fetchCustomers();

      alert('League added successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to add customer';
      console.error('Add customer error:', err);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerFormData({
      cust_name: customer.cust_name || '',
      contact: customer.contact || '',
      phone: customer.phone || '',
      addr1: customer.addr1 || '',
      addr2: customer.addr2 || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
    });
    setShowEditCustomerModal(true);
  };

  const handleCloseEditCustomerModal = () => {
    setShowEditCustomerModal(false);
    setSelectedCustomer(null);
    setCustomerFormData({
      cust_name: '',
      contact: '',
      phone: '',
      addr1: '',
      addr2: '',
      city: '',
      state: '',
      zip: '',
    });
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();

    // Validate required field
    if (!customerFormData.cust_name.trim()) {
      alert('League name is required');
      return;
    }

    try {
      setLoading(true);
      await api.put(`/league-uniform-customers/${selectedCustomer.id}`, customerFormData);

      // Close modal and reset form
      handleCloseEditCustomerModal();

      // Refresh customer list
      fetchCustomers();

      alert('League updated successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update customer';
      console.error('Update customer error:', err);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;

    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete "${selectedCustomer.cust_name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await api.delete(`/league-uniform-customers/${selectedCustomer.id}`);

      // Close modal and reset form
      handleCloseEditCustomerModal();

      // Refresh customer list
      fetchCustomers();

      alert('League deleted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete customer';
      console.error('Delete customer error:', err);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/uniform-orders/orders', {
        params: { limit: 1000 },
      });
      const historyList = response.data.orders || [];
      setOrderHistory(historyList);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch order history';
      console.error('Fetch order history error:', err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Filter order history based on status and search
  const filteredHistory = orderHistory.filter((order) => {
    const matchesStatus = historyFilterStatus === 'all' || order.status === historyFilterStatus;

    // If no search term, check status only
    if (!historySearchTerm.trim()) {
      return matchesStatus;
    }

    // Search across multiple fields
    const searchLower = historySearchTerm.toLowerCase();
    const matchesSearch =
      (order.customer_name && order.customer_name.toLowerCase().includes(searchLower)) ||
      (order.team_name && order.team_name.toLowerCase().includes(searchLower)) ||
      (order.order_number && order.order_number.toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch;
  });

  // Paginate order history
  const historyTotalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const historyStartIndex = (historyCurrentPage - 1) * itemsPerPage;
  const paginatedHistory = filteredHistory.slice(historyStartIndex, historyStartIndex + itemsPerPage);

  return (
    <div className="uniform-orders-dashboard">
      <div className="dashboard-header">
        <p>Manage league uniform orders and inventory</p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button
          className={`tab-button ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Leagues
        </button>
        <button
          className={`tab-button ${activeTab === 'order-history' ? 'active' : ''}`}
          onClick={() => setActiveTab('order-history')}
        >
          Order-History
        </button>
        <button
          className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
      </div>

      {/* Orders Tab Content */}
      {activeTab === 'orders' && (
        <>
          {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card stat-card-total">
          <div className="stat-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14h-2v-4H8v-2h2V9h2v2h2v2h-2v4zm4-8h-2V7h2v2z"/>
            </svg>
          </div>
          <div className="stat-card-content">
            <p className="stat-label">Total Orders</p>
            <p className="stat-value">{filteredOrders.length}</p>
            <p className="stat-subtext">All time orders</p>
          </div>
        </div>
        <div className="stat-card stat-card-value">
          <div className="stat-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
            </svg>
          </div>
          <div className="stat-card-content">
            <p className="stat-label">Total Value</p>
            <p className="stat-value">{formatCurrency(filteredOrders.reduce((sum, o) => sum + getOrderTotal(o), 0))}</p>
            <p className="stat-subtext">Revenue generated</p>
          </div>
        </div>
        <div className="stat-card stat-card-pending">
          <div className="stat-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
            </svg>
          </div>
          <div className="stat-card-content">
            <p className="stat-label">Pending</p>
            <p className="stat-value">{filteredOrders.filter((o) => o.status === 'Pending').length}</p>
            <p className="stat-subtext">Awaiting action</p>
          </div>
        </div>
        <div className="stat-card stat-card-production">
          <div className="stat-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
            </svg>
          </div>
          <div className="stat-card-content">
            <p className="stat-label">In Production</p>
            <p className="stat-value">{filteredOrders.filter((o) => o.status === 'In Production').length}</p>
            <p className="stat-subtext">Being processed</p>
          </div>
        </div>
        <div className="stat-card stat-card-completed">
          <div className="stat-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="stat-card-content">
            <p className="stat-label">Completed</p>
            <p className="stat-value">{filteredOrders.filter((o) => o.status === 'Completed').length}</p>
            <p className="stat-subtext">Successfully delivered</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="controls-top-row">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search by customer, team, or order number..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Production">In Production</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <button onClick={handlePrintBlankForm} className="btn-secondary" title="Printable form to hand-fill with a customer, then photograph and send to Jake">
            Print Blank Order Form
          </button>

          <button onClick={handleCreateOrder} className="btn-primary">
            + Create Order
          </button>
        </div>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : paginatedOrders.length === 0 ? (
        <div className="no-data">
          <p>No orders found. {searchTerm || filterStatus !== 'all' ? 'Try adjusting your filters.' : 'Create your first order!'}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>League</th>
                  <th>Quantity</th>
                  <th>Total Price</th>
                  <th>Status</th>
                  <th>Order Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="order-number">{order.order_number}</td>
                    <td>{order.customer_name}</td>
                    <td>{getOrderItemCount(order)}</td>
                    <td className="total-price">{formatCurrency(getOrderTotal(order))}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor:
                            order.status === 'Completed' ? '#228B22' :
                            order.status === 'In Production' ? '#667eea' :
                            order.status === 'Pending' ? '#FFA500' : '#999'
                        }}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td>{new Date(order.order_date).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="btn-view"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="orders-mobile-list">
            {paginatedOrders.map((order) => (
              <div key={order.id} className="uo-order-card">
                <div className="uo-card-header">
                  <span className="uo-card-title">{order.order_number}</span>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor:
                        order.status === 'Completed' ? '#228B22' :
                        order.status === 'In Production' ? '#667eea' :
                        order.status === 'Pending' ? '#FFA500' : '#999'
                    }}
                  >
                    {order.status}
                  </span>
                </div>
                <div className="uo-card-body">
                  <div className="uo-card-row">
                    <span className="uo-card-label">League</span>
                    <span className="uo-card-value">{order.customer_name}</span>
                  </div>
                  <div className="uo-card-row">
                    <span className="uo-card-label">Quantity</span>
                    <span className="uo-card-value">{getOrderItemCount(order)}</span>
                  </div>
                  <div className="uo-card-row">
                    <span className="uo-card-label">Total</span>
                    <span className="uo-card-value uo-card-total">{formatCurrency(getOrderTotal(order))}</span>
                  </div>
                </div>
                <div className="uo-card-footer">
                  <span className="uo-card-date">{new Date(order.order_date).toLocaleDateString()}</span>
                  <button onClick={() => handleViewOrder(order)} className="btn-view">
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-pagination"
              >
                ← Previous
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn-pagination"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
        </>
      )}

      {/* Customers Tab Content */}
      {activeTab === 'customers' && (
        <div className="customers-section">
          <div className="controls-section">
            <div className="controls-top-row">
              <div className="search-filter">
                <input
                  type="text"
                  placeholder="Search by name, contact, city, or phone..."
                  value={customerSearchTerm}
                  onChange={(e) => {
                    setCustomerSearchTerm(e.target.value);
                    setCustomerCurrentPage(1);
                  }}
                  className="search-input"
                />
              </div>
              <button onClick={handleAddCustomer} className="btn-primary">
                + Add League
              </button>
            </div>
          </div>

          {/* Customers Table */}
          {loading ? (
            <div className="loading">Loading leagues...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : customers.length === 0 ? (
            <div className="no-data">
              <p>No leagues found. {customerSearchTerm ? 'Try adjusting your search.' : 'Add your first league to get started!'}</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>League Name</th>
                      <th>Contact</th>
                      <th>Phone</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id}>
                        <td className="customer-name">{customer.cust_name}</td>
                        <td>{customer.contact || '-'}</td>
                        <td>{customer.phone || '-'}</td>
                        <td>{customer.city || '-'}</td>
                        <td>{customer.state || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleViewCustomer(customer)}
                            className="btn-view"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="customers-mobile-list">
                {customers.map((customer) => (
                  <div key={customer.id} className="uo-customer-card">
                    <div className="uo-card-header">
                      <span className="uo-card-title">{customer.cust_name}</span>
                      <button onClick={() => handleViewCustomer(customer)} className="btn-view">
                        View
                      </button>
                    </div>
                    <div className="uo-card-body">
                      <div className="uo-card-row">
                        <span className="uo-card-label">Contact</span>
                        <span className="uo-card-value">{customer.contact || '-'}</span>
                      </div>
                      <div className="uo-card-row">
                        <span className="uo-card-label">Phone</span>
                        <span className="uo-card-value">{customer.phone || '-'}</span>
                      </div>
                      <div className="uo-card-row">
                        <span className="uo-card-label">Location</span>
                        <span className="uo-card-value">
                          {customer.city ? `${customer.city}, ${customer.state || ''}` : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {customerTotalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCustomerCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={customerCurrentPage === 1}
                    className="btn-pagination"
                  >
                    ← Previous
                  </button>

                  <span className="page-info">
                    Page {customerCurrentPage} of {customerTotalPages}
                  </span>

                  <button
                    onClick={() => setCustomerCurrentPage((p) => Math.min(customerTotalPages, p + 1))}
                    disabled={customerCurrentPage === customerTotalPages}
                    className="btn-pagination"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Order-History Tab Content */}
      {activeTab === 'order-history' && (
        <div className="order-history-section">
          <div className="controls-section">
            <div className="controls-top-row">
              <div className="search-filter">
                <input
                  type="text"
                  placeholder="Search by customer, team, or order number..."
                  value={historySearchTerm}
                  onChange={(e) => {
                    setHistorySearchTerm(e.target.value);
                    setHistoryCurrentPage(1);
                  }}
                  className="search-input"
                />

                <select
                  value={historyFilterStatus}
                  onChange={(e) => {
                    setHistoryFilterStatus(e.target.value);
                    setHistoryCurrentPage(1);
                  }}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="In Production">In Production</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Order History Table */}
          {loading ? (
            <div className="loading">Loading order history...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : paginatedHistory.length === 0 ? (
            <div className="no-data">
              <p>No orders found. {historySearchTerm || historyFilterStatus !== 'all' ? 'Try adjusting your filters.' : 'No order history available.'}</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>League</th>
                      <th>Quantity</th>
                      <th>Total Price</th>
                      <th>Status</th>
                      <th>Order Date</th>
                      <th>Due Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHistory.map((order) => (
                      <tr key={order.id}>
                        <td className="order-number">{order.order_number}</td>
                        <td>{order.customer_name}</td>
                        <td>{getOrderItemCount(order)}</td>
                        <td className="total-price">{formatCurrency(getOrderTotal(order))}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor:
                                order.status === 'Completed' ? '#228B22' :
                                order.status === 'In Production' ? '#667eea' :
                                order.status === 'Pending' ? '#FFA500' : '#999'
                            }}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td>{new Date(order.order_date).toLocaleDateString()}</td>
                        <td>{order.due_date ? new Date(order.due_date).toLocaleDateString() : '-'}</td>
                        <td>
                          <button
                            onClick={() => handleViewOrder(order)}
                            className="btn-view"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="orders-mobile-list">
                {paginatedHistory.map((order) => (
                  <div key={order.id} className="uo-order-card">
                    <div className="uo-card-header">
                      <span className="uo-card-title">{order.order_number}</span>
                      <span
                        className="status-badge"
                        style={{
                          backgroundColor:
                            order.status === 'Completed' ? '#228B22' :
                            order.status === 'In Production' ? '#667eea' :
                            order.status === 'Pending' ? '#FFA500' : '#999'
                        }}
                      >
                        {order.status}
                      </span>
                    </div>
                    <div className="uo-card-body">
                      <div className="uo-card-row">
                        <span className="uo-card-label">League</span>
                        <span className="uo-card-value">{order.customer_name}</span>
                      </div>
                      <div className="uo-card-row">
                        <span className="uo-card-label">Quantity</span>
                        <span className="uo-card-value">{getOrderItemCount(order)}</span>
                      </div>
                      <div className="uo-card-row">
                        <span className="uo-card-label">Total</span>
                        <span className="uo-card-value uo-card-total">{formatCurrency(getOrderTotal(order))}</span>
                      </div>
                      <div className="uo-card-row">
                        <span className="uo-card-label">Due Date</span>
                        <span className="uo-card-value">{order.due_date ? new Date(order.due_date).toLocaleDateString() : '-'}</span>
                      </div>
                    </div>
                    <div className="uo-card-footer">
                      <span className="uo-card-date">{new Date(order.order_date).toLocaleDateString()}</span>
                      <button onClick={() => handleViewOrder(order)} className="btn-view">
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {historyTotalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setHistoryCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={historyCurrentPage === 1}
                    className="btn-pagination"
                  >
                    ← Previous
                  </button>

                  <span className="page-info">
                    Page {historyCurrentPage} of {historyTotalPages}
                  </span>

                  <button
                    onClick={() => setHistoryCurrentPage((p) => Math.min(historyTotalPages, p + 1))}
                    disabled={historyCurrentPage === historyTotalPages}
                    className="btn-pagination"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Reports Tab Content */}
      {activeTab === 'reports' && (
        <div className="reports-section">
          <BillingReport />
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div className="modal-overlay" onClick={handleCloseCustomerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New League</h2>
              <button className="modal-close" onClick={handleCloseCustomerModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitCustomer} className="customer-form">
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="cust_name">
                    League Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cust_name"
                    name="cust_name"
                    value={customerFormData.cust_name}
                    onChange={handleCustomerFormChange}
                    required
                    placeholder="Enter league name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact">Contact Person</label>
                  <input
                    type="text"
                    id="contact"
                    name="contact"
                    value={customerFormData.contact}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter contact person"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={customerFormData.phone}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="addr1">Address Line 1</label>
                  <input
                    type="text"
                    id="addr1"
                    name="addr1"
                    value={customerFormData.addr1}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter street address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="addr2">Address Line 2</label>
                  <input
                    type="text"
                    id="addr2"
                    name="addr2"
                    value={customerFormData.addr2}
                    onChange={handleCustomerFormChange}
                    placeholder="Apt, suite, unit, etc. (optional)"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={customerFormData.city}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter city"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={customerFormData.state}
                    onChange={handleCustomerFormChange}
                    placeholder="TX"
                    maxLength="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zip">ZIP Code</label>
                  <input
                    type="text"
                    id="zip"
                    name="zip"
                    value={customerFormData.zip}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter ZIP"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseCustomerModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add League
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && selectedCustomer && (
        <div className="modal-overlay" onClick={handleCloseEditCustomerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>View/Edit League</h2>
              <button className="modal-close" onClick={handleCloseEditCustomerModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateCustomer} className="customer-form">
              <div className="customer-info-section">
                <div className="info-row">
                  <span className="info-label">Created:</span>
                  <span className="info-value">
                    {new Date(selectedCustomer.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="edit_cust_name">
                    League Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="edit_cust_name"
                    name="cust_name"
                    value={customerFormData.cust_name}
                    onChange={handleCustomerFormChange}
                    required
                    placeholder="Enter league name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit_contact">Contact Person</label>
                  <input
                    type="text"
                    id="edit_contact"
                    name="contact"
                    value={customerFormData.contact}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter contact person"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_phone">Phone</label>
                  <input
                    type="tel"
                    id="edit_phone"
                    name="phone"
                    value={customerFormData.phone}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="edit_addr1">Address Line 1</label>
                  <input
                    type="text"
                    id="edit_addr1"
                    name="addr1"
                    value={customerFormData.addr1}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter street address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="edit_addr2">Address Line 2</label>
                  <input
                    type="text"
                    id="edit_addr2"
                    name="addr2"
                    value={customerFormData.addr2}
                    onChange={handleCustomerFormChange}
                    placeholder="Apt, suite, unit, etc. (optional)"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit_city">City</label>
                  <input
                    type="text"
                    id="edit_city"
                    name="city"
                    value={customerFormData.city}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter city"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_state">State</label>
                  <input
                    type="text"
                    id="edit_state"
                    name="state"
                    value={customerFormData.state}
                    onChange={handleCustomerFormChange}
                    placeholder="TX"
                    maxLength="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit_zip">ZIP Code</label>
                  <input
                    type="text"
                    id="edit_zip"
                    name="zip"
                    value={customerFormData.zip}
                    onChange={handleCustomerFormChange}
                    placeholder="Enter ZIP"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <div className="modal-footer-left">
                  <button
                    type="button"
                    className="btn-create-order"
                    onClick={() => {
                      navigate('/league-uniform-management/order-creation', {
                        state: { customer: selectedCustomer }
                      });
                    }}
                  >
                    + Create New Order
                  </button>
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={handleDeleteCustomer}
                  >
                    Delete League
                  </button>
                </div>
                <div className="modal-footer-right">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCloseEditCustomerModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Update League
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={handleCloseOrderDetailsModal}>
          <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="modal-close" onClick={handleCloseOrderDetailsModal}>
                ×
              </button>
            </div>

            <div className="order-details-content">
              {/* Order Info Section */}
              <div className="order-info-section">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Order #:</span>
                    <span className="info-value">{selectedOrder.order_number}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">League:</span>
                    <span className="info-value">{selectedOrder.customer_name}</span>
                  </div>
                  {selectedOrder.team_name && (
                    <div className="info-item">
                      <span className="info-label">Team:</span>
                      <span className="info-value">{selectedOrder.team_name}</span>
                    </div>
                  )}
                  {selectedOrder.division && (
                    <div className="info-item">
                      <span className="info-label">Division:</span>
                      <span className="info-value">{selectedOrder.division}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor:
                          selectedOrder.status === 'Completed' ? '#228B22' :
                          selectedOrder.status === 'In Production' ? '#667eea' :
                          selectedOrder.status === 'Pending' ? '#FFA500' : '#999'
                      }}
                    >
                      {selectedOrder.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Order Date:</span>
                    <span className="info-value">{new Date(selectedOrder.order_date).toLocaleDateString()}</span>
                  </div>
                  {selectedOrder.due_date && (
                    <div className="info-item">
                      <span className="info-label">Due Date:</span>
                      <span className="info-value">{new Date(selectedOrder.due_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Breakdown Section */}
              <div className="order-breakdown-section">
                <h3>Order Breakdown</h3>
                {renderOrderBreakdown(selectedOrder.order_details)}
              </div>

              {/* Order Total Section */}
              <div className="order-total-section">
                <div className="total-row">
                  <span className="total-label">Total Items:</span>
                  <span className="total-value">{getOrderItemCount(selectedOrder)}</span>
                </div>
                <div className="total-row grand-total">
                  <span className="total-label">Total Price:</span>
                  <span className="total-value">{formatCurrency(getOrderTotal(selectedOrder))}</span>
                </div>
              </div>

              {/* Notes Section */}
              {selectedOrder.notes && (
                <div className="order-notes-section">
                  <h3>Notes</h3>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left">
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => handleDeleteOrder(selectedOrder)}
                >
                  Delete Order
                </button>
              </div>
              <div className="modal-footer-right">
                <div className="print-forms-container">
                  <button
                    type="button"
                    className="btn-print"
                    onClick={() => setShowPrintMenu(!showPrintMenu)}
                  >
                    Print Forms ▾
                  </button>
                  {showPrintMenu && (
                    <div className="print-menu-dropdown">
                      <div className="print-menu-header">Select Form to Print</div>
                      {/* Checklist option - always available */}
                      <button
                        className="print-menu-item checklist-item"
                        onClick={() => handlePrintForm('checklist')}
                      >
                        <span className="print-menu-icon">✅</span>
                        <span>Checklist</span>
                      </button>
                      {getAvailablePrintCategories(selectedOrder.order_details).length > 0 ? (
                        getAvailablePrintCategories(selectedOrder.order_details).map((category) => (
                          <button
                            key={category.key}
                            className="print-menu-item"
                            onClick={() => handlePrintForm(category.key)}
                          >
                            <span className="print-menu-icon">{category.icon}</span>
                            <span>{category.label}</span>
                          </button>
                        ))
                      ) : (
                        <div className="print-menu-empty">No items in this order</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="print-labels-container">
                  <button
                    type="button"
                    className="btn-print-labels"
                    onClick={() => setShowLabelsMenu(!showLabelsMenu)}
                  >
                    Print Labels ▾
                  </button>
                  {showLabelsMenu && (
                    <div className="print-labels-dropdown">
                      <div className="print-menu-header">Select Labels to Print</div>
                      {getAvailablePrintCategories(selectedOrder.order_details).length > 0 ? (
                        getAvailablePrintCategories(selectedOrder.order_details).map((category) => (
                          <button
                            key={category.key}
                            className="print-menu-item"
                            onClick={() => handlePrintLabels(category.key)}
                          >
                            <span className="print-menu-icon">{category.icon}</span>
                            <span>{category.label} Labels</span>
                          </button>
                        ))
                      ) : (
                        <div className="print-menu-empty">No items in this order</div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-edit"
                  onClick={() => {
                    handleCloseOrderDetailsModal();
                    navigate('/league-uniform-management/order-creation', {
                      state: { editOrder: selectedOrder }
                    });
                  }}
                >
                  Edit Order
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseOrderDetailsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
